import { ProductAttribute, ProductConfiguration } from "@wttb/product-configurator";
import { parseAsJson, useQueryState } from "nuqs";
import { useMemo, useState } from "react";

import { ChoiceButton } from "@/common/components/form/choice-button";
import { FlagType } from "@/features/product/components/product-attributes/product-attributes-renderer";
import { useStoreProduct } from "@/features/product/contexts/product-context";
import { SizeCustomForm } from "./custom-size-attribute/size-custom-form";

export type SingleOption = {
  id: string;
  name: string;
  value: string;
  flags?: FlagType[];
  disabled: boolean;
  isIncompatible?: boolean;
};

interface SingleChoiceButtonSelectorTypes {
  options: SingleOption[];
  attribute: ProductAttribute;
  onChange: (valueId: string) => void;
}

export function SingleChoiceButtonSelector({
  options,
  onChange,
  attribute,
}: SingleChoiceButtonSelectorTypes) {
  const { config } = useStoreProduct();
  const [selectedOptions] = useQueryState<ProductConfiguration & { size?: string }>(
    "selectedOptions",
    parseAsJson<ProductConfiguration & { size?: string }>().withDefault({}),
  );
  const [isFormVisible, setIsFormVisible] = useState(/^\d+x\d+$/.test(selectedOptions?.size || ""));

  const handleOnInputChange = (valueId: string) => {
    onChange(valueId);
    if (isFormVisible) setIsFormVisible(false);
  };

  const isCustomSizeFormVisible = useMemo(() => {
    if (!(attribute.custom && attribute.key === "size")) return false;
    const { minHeight, maxHeight, minWidth, maxWidth } = attribute;
    const settings = [minHeight, maxHeight, minWidth, maxWidth].map(Number);
    if (settings.some(isNaN)) return false;
    if (settings.some((v) => v <= 0)) return false;
    return true;
  }, [attribute]);

  return (
    <>
      <div className="grid grid-cols-2 gap-6 text-center">
        {options?.map((option: SingleOption) => (
          <ChoiceButton
            {...option}
            onInputChange={() => handleOnInputChange(option.id)}
            key={option.id}
            isChecked={option.value === config![attribute.key]}
            type="radio"
            disabled={option.disabled}
          />
        ))}
        {isCustomSizeFormVisible && (
          <ChoiceButton
            name="Custom"
            value="custom"
            onInputChange={() => setIsFormVisible(true)}
            isChecked={isFormVisible}
            type="radio"
          />
        )}
        {isCustomSizeFormVisible && isFormVisible && (
          <SizeCustomForm className="col-start-1 col-end-3" attribute={attribute} />
        )}
      </div>
    </>
  );
}
